import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {styled} from 'styled-components';

import {resolveViewerParts} from '@shared/lib/cad/manifest_mapping';
import {CadModelManifest, CadViewerPart} from '@shared/models/cad_model';

import {MEDIA_QUERY_MOBILE_ONLY} from '@shared_web/breakpoints';
import {LoadingIndicator} from '@shared_web/components/core/loading_indicator';
import {Flex} from '@shared_web/components/core2/flex';

import {ExplodeControls} from '@mavismart_frontend/components/door_3d/explode_controls';
import {
  NavigationToolbar,
  NavMode,
} from '@mavismart_frontend/components/door_3d/navigation_toolbar';
import {PartDetails} from '@mavismart_frontend/components/door_3d/part_details';
import {PartList} from '@mavismart_frontend/components/door_3d/part_list';
import {Translate} from '@mavismart_frontend/components/translate';
import {LIGHT_GRAY_COLOR, MAIN_COLOR, TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {ViewerEngine, ViewerTheme} from '@mavismart_frontend/lib/door_3d/viewer_engine';

const VIEWER_THEME: ViewerTheme = {
  background: '#eef1f4',
  selection: MAIN_COLOR,
  hover: '#ffffff',
};

export interface Door3dViewerProps {
  /** URL of the GLB. Resolved by the caller (the API), never hardcoded in this component. */
  modelUrl: string;
  /**
   * Business metadata for the parts. Passed in rather than read from a registry so that the same
   * component can later be driven by an API response without any change here.
   */
  manifest: CadModelManifest | undefined;
  /** Controlled selection, when the page needs to drive it (from an alert, a deep link, ...). */
  selectedPartId?: string | undefined;
  onSelectPart?: (partId: string | undefined) => void;
  onError?: (error: Error) => void;
  /**
   * Called once the GLB is parsed, with how well the manifest matched the geometry. Lets the page
   * surface a drift between manifest and model without this component deciding how.
   */
  onLoaded?: (info: {parts: CadViewerPart[]; unmappedNodeNames: string[]}) => void;
}

type Status =
  | {kind: 'loading'; progress: number | undefined}
  | {kind: 'ready'}
  | {kind: 'error'; message: string}
  | {kind: 'context-lost'};

/**
 * Interactive 3D viewer for a door: orbit, hover, select, and an animated exploded view.
 *
 * Deliberate boundaries:
 *  * geometry comes from `modelUrl` (the API decides where from and for how long),
 *  * business metadata comes from `manifest`,
 *  * live state would come from a third source and is not wired yet.
 *
 * Nothing here knows which door it is showing.
 */
export const Door3dViewer: FC<Door3dViewerProps> = props => {
  const {modelUrl, manifest, selectedPartId, onSelectPart, onError, onLoaded} = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<ViewerEngine | undefined>(undefined);

  const [status, setStatus] = useState<Status>({kind: 'loading', progress: undefined});
  const [parts, setParts] = useState<CadViewerPart[]>([]);
  const [internalSelectedId, setInternalSelectedId] = useState<string | undefined>(selectedPartId);
  const [hoveredId, setHoveredId] = useState<string | undefined>();
  const [explodePercent, setExplodePercent] = useState(50);
  const [navMode, setNavMode] = useState<NavMode>('orbit');

  // Controlled when the page passes `selectedPartId`, self-managed otherwise.
  const isControlled = selectedPartId !== undefined || onSelectPart !== undefined;
  const effectiveSelectedId = isControlled ? selectedPartId : internalSelectedId;

  // Callbacks are read through a ref so that the engine can be created once per model URL rather
  // than being torn down every time the parent re-renders with new closures.
  const handlersRef = useRef({onError, onLoaded, onSelectPart});
  useEffect(() => {
    handlersRef.current = {onError, onLoaded, onSelectPart};
  }, [onError, onLoaded, onSelectPart]);

  const manifestRef = useRef(manifest);
  useEffect(() => {
    manifestRef.current = manifest;
  }, [manifest]);

  //
  // Engine lifecycle: one engine per model URL, fully torn down on unmount.
  //
  useEffect(() => {
    const container = containerRef.current;
    if (container === null) {
      return undefined;
    }

    let cancelled = false;
    setStatus({kind: 'loading', progress: undefined});
    setParts([]);

    const engine = new ViewerEngine(container, VIEWER_THEME, {
      onHoverChange: partId => {
        if (!cancelled) {
          setHoveredId(partId);
        }
      },
      onSelectChange: partId => {
        if (cancelled) {
          return;
        }
        setInternalSelectedId(partId);
        handlersRef.current.onSelectPart?.(partId);
      },
      onContextLost: () => {
        if (!cancelled) {
          setStatus({kind: 'context-lost'});
        }
      },
    });
    engineRef.current = engine;

    engine
      .load(modelUrl, ratio => {
        if (!cancelled) {
          setStatus({kind: 'loading', progress: ratio});
        }
      })
      .then(({nodeNames, hasOwnColors}) => {
        if (cancelled) {
          return;
        }
        const {parts: resolved, diagnostic} = resolveViewerParts(manifestRef.current, nodeNames);
        // Tints are only a legibility aid for models that carry no colour of their own.
        engine.setParts(resolved, {applyTints: !hasOwnColors});
        // Apply the initial explode ratio before framing so the camera fits the exploded state.
        engine.setExplodeRatio(0.5, {animate: false});
        engine.frameAll();
        setParts(resolved);
        setStatus({kind: 'ready'});
        handlersRef.current.onLoaded?.({
          parts: resolved,
          unmappedNodeNames: diagnostic.unmappedNodeNames,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        const error = err instanceof Error ? err : new Error(String(err));
        setStatus({kind: 'error', message: error.message});
        handlersRef.current.onError?.(error);
      });

    return () => {
      cancelled = true;
      engineRef.current = undefined;
      engine.dispose();
    };
  }, [modelUrl]);

  //
  // Push UI state into the scene.
  //
  useEffect(() => {
    engineRef.current?.setSelected(effectiveSelectedId);
  }, [effectiveSelectedId]);

  useEffect(() => {
    engineRef.current?.setHovered(hoveredId);
  }, [hoveredId]);

  useEffect(() => {
    engineRef.current?.setExplodeRatio(explodePercent / 100);
  }, [explodePercent]);

  const handleRecenter = useCallback(() => engineRef.current?.frameAll(), []);

  const handleNavModeChange = useCallback((mode: NavMode) => {
    setNavMode(mode);
    engineRef.current?.setNavMode(mode);
  }, []);

  const handleZoomIn = useCallback(() => engineRef.current?.zoom(1), []);
  const handleZoomOut = useCallback(() => engineRef.current?.zoom(-1), []);
  const handlePresetView = useCallback(
    (preset: 'front' | 'back' | 'top' | 'bottom' | 'right' | 'left') =>
      engineRef.current?.setPresetView(preset),
    []
  );

  const handleSelectFromList = useCallback((partId: string | undefined) => {
    setInternalSelectedId(partId);
    handlersRef.current.onSelectPart?.(partId);
    if (partId !== undefined) {
      engineRef.current?.framePart(partId);
    }
  }, []);

  const handleHoverFromList = useCallback((partId: string | undefined) => {
    setHoveredId(partId);
  }, []);

  const selectedPart = useMemo(
    () => parts.find(part => part.id === effectiveSelectedId),
    [parts, effectiveSelectedId]
  );

  const isBusy = status.kind !== 'ready';

  return (
    <Layout>
      <ScenePane>
        <Canvas ref={containerRef} aria-hidden="true" />

        {status.kind === 'loading' && (
          <Overlay>
            <LoadingIndicator />
            <OverlayText>
              <Translate value="door_3d__loading" />
              {status.progress !== undefined && ` ${Math.round(status.progress * 100)}%`}
            </OverlayText>
          </Overlay>
        )}

        {status.kind === 'error' && (
          <Overlay role="alert">
            <OverlayTitle>
              <Translate value="door_3d__load_error" />
            </OverlayTitle>
            <OverlayDetail>{status.message}</OverlayDetail>
          </Overlay>
        )}

        {status.kind === 'context-lost' && (
          <Overlay role="alert">
            <OverlayTitle>
              <Translate value="door_3d__context_lost" />
            </OverlayTitle>
            <OverlayDetail>
              <Translate value="door_3d__context_lost_hint" />
            </OverlayDetail>
          </Overlay>
        )}

        <NavigationToolbar
          mode={navMode}
          onModeChange={handleNavModeChange}
          onPresetView={handlePresetView}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          disabled={isBusy}
        />

        <ControlsBar>
          <ExplodeControls
            percent={explodePercent}
            onPercentChange={setExplodePercent}
            onRecenter={handleRecenter}
            disabled={isBusy}
          />
        </ControlsBar>
      </ScenePane>

      <SidePane $column>
        <PartDetails part={selectedPart} />
        <PartList
          parts={parts}
          selectedPartId={effectiveSelectedId}
          hoveredPartId={hoveredId}
          onSelect={handleSelectFromList}
          onHover={handleHoverFromList}
        />
      </SidePane>
    </Layout>
  );
};

Door3dViewer.displayName = 'Door3dViewer';

const Layout = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  min-height: 0;
  flex-grow: 1;
  padding: 16px;

  ${MEDIA_QUERY_MOBILE_ONLY} {
    /* On a narrow screen the scene keeps a usable height and the panel becomes a sheet below it. */
    flex-direction: column;
    padding: 8px;
  }
`;

const ScenePane = styled.div`
  position: relative;
  flex-grow: 1;
  min-width: 0;
  min-height: 320px;
  border-radius: 8px;
  overflow: hidden;
  border: solid 1px ${LIGHT_GRAY_COLOR};

  ${MEDIA_QUERY_MOBILE_ONLY} {
    min-height: 45vh;
    border-radius: 6px;
  }
`;

const Canvas = styled.div`
  position: absolute;
  inset: 0;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  text-align: center;
  background-color: #eef1f4e6;
  color: ${TEXT_MAIN_COLOR};
`;

const OverlayText = styled.div`
  font-size: 13px;
`;

const OverlayTitle = styled.div`
  font-weight: 700;
`;

const OverlayDetail = styled.div`
  font-size: 12px;
  color: ${TEXT_MAIN_COLOR}aa;
  max-width: 420px;
  overflow-wrap: anywhere;
`;

const ControlsBar = styled.div`
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 10px;
  padding: 10px;
  border-radius: 8px;
  background-color: #ffffffdd;
  backdrop-filter: blur(4px);
  border: solid 1px ${LIGHT_GRAY_COLOR};

  ${MEDIA_QUERY_MOBILE_ONLY} {
    left: 6px;
    right: 6px;
    bottom: 6px;
    padding: 6px 8px;
  }
`;

const SidePane = styled(Flex)`
  width: 300px;
  min-width: 0;
  flex-shrink: 0;
  gap: 10px;

  ${MEDIA_QUERY_MOBILE_ONLY} {
    width: 100%;
    max-height: 35vh;
    gap: 6px;
  }
`;
